import React, { useEffect, useState } from "react";
import { api, CITIES, SERVICES, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const empty = {
  name: "", phone: "", email: "", password: "", city: "",
  service_areas: [], available_days: ["Mon","Tue","Wed","Thu","Fri"],
  available_hours: "09:00 - 17:00", services: [], languages: ["English","Dutch"],
  has_transport: true, has_supplies: true, has_equipment: true,
  hourly_rate: 25, reliability_score: 5, quality_score: 5, notes: "", active: true,
};

const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

export default function Cleaners() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ city: "", active: "", service: "" });
  const [openId, setOpenId] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const q = new URLSearchParams();
    if (filter.city) q.append("city", filter.city);
    if (filter.active !== "") q.append("active", filter.active);
    if (filter.service) q.append("service", filter.service);
    const r = await api.get(`/providers?${q}`);
    setItems(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const save = async () => {
    try {
      const payload = { ...form, hourly_rate: parseFloat(form.hourly_rate) || 0 };
      if (openId === "new") {
        await api.post("/providers", payload);
        toast.success("Cleaner added — default password: Cleaner2026!");
      } else {
        await api.patch(`/providers/${openId}`, payload);
        toast.success("Cleaner updated");
      }
      setOpenId(null); load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this cleaner?")) return;
    await api.delete(`/providers/${id}`);
    load();
  };

  const toggle = (arr, v) => arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1B3A6B]">Cleaners</h1>
          <p className="text-sm text-slate-500 mt-1">Your cleaning providers.</p>
        </div>
        <Button onClick={() => { setOpenId("new"); setForm(empty); }} className="bg-[#1B3A6B] hover:bg-[#152D54] text-white rounded-full" data-testid="cleaner-new-btn">
          <Plus className="w-4 h-4 mr-1" /> New Cleaner
        </Button>
      </div>

      <div className="mt-5 grid sm:grid-cols-3 gap-3 max-w-3xl">
        <Select value={filter.city} onValueChange={(v) => setFilter({ ...filter, city: v === "all" ? "" : v })}>
          <SelectTrigger><SelectValue placeholder="All cities" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All cities</SelectItem>
            {CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.active} onValueChange={(v) => setFilter({ ...filter, active: v === "any" ? "" : v })}>
          <SelectTrigger><SelectValue placeholder="Any status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any status</SelectItem>
            <SelectItem value="true">Active</SelectItem>
            <SelectItem value="false">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filter.service} onValueChange={(v) => setFilter({ ...filter, service: v === "any" ? "" : v })}>
          <SelectTrigger><SelectValue placeholder="Any service" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any service</SelectItem>
            {SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 bg-white rounded-xl ring-1 ring-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>City</TableHead><TableHead>Services</TableHead>
              <TableHead>Rate</TableHead><TableHead>Reliability</TableHead><TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((p) => (
              <TableRow key={p.id}>
                <TableCell>
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-slate-500">{p.email} · {p.phone}</div>
                </TableCell>
                <TableCell>{p.city}</TableCell>
                <TableCell className="text-xs text-slate-600">{(p.services || []).slice(0,3).join(", ")}{(p.services || []).length > 3 ? "…" : ""}</TableCell>
                <TableCell>€{p.hourly_rate}/h</TableCell>
                <TableCell>{p.reliability_score?.toFixed(1)}</TableCell>
                <TableCell>
                  <span className={`pill ${p.active ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : "bg-slate-100 text-slate-600"}`}>
                    {p.active ? "Active" : "Inactive"}
                  </span>
                </TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => { setOpenId(p.id); setForm({ ...empty, ...p, password: "" }); }}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(p.id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500">No cleaners.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{openId === "new" ? "New cleaner" : "Edit cleaner"}</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="cleaner-form-name" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Email (login)</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="cleaner-form-email" /></div>
            <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5" /></div>
            {openId === "new" && (
              <div className="md:col-span-2"><Label>Password (optional, default: Cleaner2026!)</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" /></div>
            )}
            <div><Label>Hourly rate (€)</Label><Input type="number" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Available hours</Label><Input value={form.available_hours} onChange={(e) => setForm({ ...form, available_hours: e.target.value })} className="mt-1.5" /></div>
          </div>

          <div>
            <Label>Available days</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {DAYS.map((d) => (
                <label key={d} className={`px-3 py-1 rounded-full text-sm cursor-pointer border ${form.available_days.includes(d) ? "border-[#5BA83F] bg-[#E8F5E0] text-[#3F7A1F]" : "border-slate-200"}`}>
                  <Checkbox className="sr-only" checked={form.available_days.includes(d)} onCheckedChange={() => setForm({ ...form, available_days: toggle(form.available_days, d) })} /> {d}
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label>Services they can do</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {SERVICES.map((s) => (
                <button type="button" key={s} onClick={() => setForm({ ...form, services: toggle(form.services, s) })}
                  className={`px-3 py-1 rounded-full text-xs border ${form.services.includes(s) ? "border-[#5BA83F] bg-[#E8F5E0] text-[#3F7A1F]" : "border-slate-200 text-slate-600"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <ToggleRow label="Has transport" value={form.has_transport} onChange={(v) => setForm({ ...form, has_transport: v })} />
            <ToggleRow label="Has supplies" value={form.has_supplies} onChange={(v) => setForm({ ...form, has_supplies: v })} />
            <ToggleRow label="Has equipment" value={form.has_equipment} onChange={(v) => setForm({ ...form, has_equipment: v })} />
          </div>

          <ToggleRow label="Active" value={form.active} onChange={(v) => setForm({ ...form, active: v })} />

          <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenId(null)}>Cancel</Button>
            <Button onClick={save} className="bg-[#1B3A6B] hover:bg-[#152D54] text-white" data-testid="cleaner-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ToggleRow({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between bg-slate-50 rounded-md px-3 py-2">
      <Label className="text-sm">{label}</Label>
      <Switch checked={value} onCheckedChange={onChange} />
    </div>
  );
}
